import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiRequests from "../../Component/axios/config/config";
import Loading from "../../Component/Loading/Loading";
import { Pencil } from "lucide-react";
import { BsImage } from "react-icons/bs";
import { IoCloseCircle } from "react-icons/io5";
import toast from "react-hot-toast";
import { type } from "@testing-library/user-event/dist/type";

export default function AdminProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);

  const [typeSeller, setTypeSeller] = useState(false);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [nationalities, setNationalities] = useState([]);

  const [showCategory, setShowCategory] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [showTaste, setShowTaste] = useState(false);
  const [showNationality, setShowNationality] = useState(false);

  const [formData, setFormData] = useState({
    type: "",
    name: "",
    status: false,
    price: "",
    categoryId: "",
    count: "",
    description: "",
    tastes: [],
    calorie: "",
    brand: "",
    nationality: "",
    isBulk: false,
  });

  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);

  const tastesList = ["Sweet", "Sour", "Salty", "Bitter", "Umami", "Spicy"];

  useEffect(() => {
    const fetchProfileAndProduct = async () => {
      try {
        const profileRes = await apiRequests.get(`/seller/v1/get-my-profile`);
        if (profileRes.data.success) {
          const isSeller = profileRes.data.data.Type === "SELLER";
          setTypeSeller(isSeller);
        }

        const productRes = await apiRequests.get(`/product/get-single/${id}`);
        const prod = productRes.data.data;

        setProduct(prod);

        setFormData({
          type: prod.type || "",
          name: prod.name || "",
          status: prod.is_exists || false,
          price: prod.price || "",
          categoryId: prod.category?._id || "",
          count: prod.stock || "",
          description: prod.description || "",
          tastes: prod.tastes || [],
          calorie: prod.calorie || "",
          brand: prod.brand?._id || prod.brand || "",
          nationality: prod.nationality || "",
          isBulk: prod.isBulk || false,
        });

        if (prod.images?.length) {
          const imagesWithPreview = prod.images.map((img) => ({
            id: img._id,
            preview: `https://api.clansmart.com/${img.fileName}`,
          }));
          setUploadedImages(imagesWithPreview);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileAndProduct();
  }, [id]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await apiRequests.get("/brand/get-all");
        if (res.data.success) setBrands(res.data.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const endpoint = typeSeller
          ? "/category/get-all-user"
          : "/category/get-all-Food-user";
        const res = await apiRequests.get(endpoint, { params: { limit: 100 } });
        if (res.data.success) setCategories(res.data.data.docs);
      } catch (e) {
        console.error(e);
      }
    };
    if (typeSeller !== null) fetchCategories();
  }, [typeSeller]);

  useEffect(() => {
    const fetchNationalities = async () => {
      try {
        const res = await apiRequests.get(
          "/nationality/v1/get-all-nationality-seller",
        );
        if (res.data.success) setNationalities(res.data.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchNationalities();
  }, []);

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (uploadedImages.length + files.length > 4) {
      alert("You can only upload a maximum of 4 images.");
      return;
    }

    setLoadingImage(true);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      try {
        const res = await apiRequests.post(
          "/file/v1/upload-seller",
          formDataUpload,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (res.data.success) {
          const imgData = res.data.message;
          setUploadedImages((prev) => [
            ...prev,
            {
              id: imgData._id,
              preview: URL.createObjectURL(file),
            },
          ]);
        }
      } catch (error) {
        console.error("Image upload failed", error);
        toast.error("Failed to upload image");
      }
    }
    setLoadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCategorySelect = (id) => {
    setFormData((prev) => ({ ...prev, categoryId: id }));
    setShowCategory(false);
  };

  const handleBrandSelect = (id) => {
    setFormData((prev) => ({ ...prev, brand: id }));
    setShowBrand(false);
  };

  const handleTasteSelect = (taste) => {
    if (formData.tastes.includes(taste)) return;
    setFormData((prev) => ({
      ...prev,
      tastes: [...prev.tastes, taste],
    }));
  };

  const removeTaste = (taste) => {
    setFormData((prev) => ({
      ...prev,
      tastes: prev.tastes.filter((t) => t !== taste),
    }));
  };

  const handleNationalitySelect = (id) => {
    setFormData((prev) => ({
      ...prev,
      nationality: id,
    }));
    setShowNationality(false);
  };

  const handleSubmit = async () => {
    if (
      !formData.name ||
      !formData.categoryId ||
      !formData.price ||
      !formData.count
    ) {
      toast.error(
        "Please fill in all required fields (Name, Price, Category, Count).",
      );
      return;
    }

    setSubmitting(true);

    const payload = {
      id: id,
      name: formData.name,
      price: Number(formData.price),
      stock: Number(formData.count),
      description: formData.description,
      category: formData.categoryId,
      brand: formData.brand,
      nationality: formData.nationality,
      tastes: formData.tastes,
      active: formData.status,
      images: uploadedImages.map((img) => img.id),
      isBulk: formData.isBulk,
    };
    const endpoint = typeSeller
      ? `/product/update-seller-product`
      : `/product/update-seller-food`;
    try {
      const res = await apiRequests.patch(endpoint, payload);
      if (res.data.success) {
        toast.success("Product updated successfully!");
        navigate(-1);
      }
    } catch (error) {
      console.error("Update failed", error);
      toast.error("Failed to update product");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="p-12">
        <Loading />
      </div>
    );
  if (!product)
    return (
      <div className="text-center font-bold text-xl text-gray-500 mt-20">
        Product not found
      </div>
    );

  return (
    <div className="w-full min-h-screen bg-white p-6 md:p-10 font-inter text-[#5c4a40]">
      <div className="flex justify-between items-center mb-8 sm:flex-col sm:items-stretch sm:gap-4">
        <h1 className="text-2xl font-bold text-[#051F33]">Edit Product</h1>
        
      </div>

      <section className="flex gap-6 mb-8 h-48 sm:flex-wrap sm:h-auto">
        {uploadedImages.map((img, index) => (
          <div
            key={img.id}
            className="relative w-48 h-full rounded-[30px] border-2 border-dashed border-[#AE9C91] overflow-hidden group sm:w-32 sm:h-32"
          >
            <img
              src={img.preview}
              alt="product"
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => removeImage(index)}
              className="absolute top-2 right-2 text-red-500 bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
            >
              <IoCloseCircle size={24} />
            </button>
          </div>
        ))}

        {uploadedImages.length < 4 && (
          <div
            onClick={() => fileInputRef.current.click()}
            className="w-48 h-full rounded-[30px] border-2 border-dashed border-[#AE9C91] flex flex-col items-center justify-center cursor-pointer hover:bg-[#efe5df]/50 transition text-[#8d7b6f] sm:w-32 sm:h-32"
          >
            {loadingImage ? (
              <span>Uploading...</span>
            ) : (
              <>
                <BsImage className="text-3xl mb-2" />
                <span className="font-medium">Upload image</span>
              </>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
              accept="image/*"
              multiple
            />
          </div>
        )}
      </section>

      <div className="bg-[#efe5df] rounded-[30px] p-8 w-full shadow-sm flex flex-col gap-6 mb-6">
        <div className="grid grid-cols-12 gap-6 items-center sm:grid-cols-1">
          <div className="col-span-8 flex-1 w-full flex flex-col gap-2 sm:col-span-1">
            <label className="ml-2 font-medium opacity-80">Name:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="h-12 rounded-xl border-none outline-none px-4 w-full shadow-sm"
            />
          </div>

          <div className="col-span-4 flex justify-center items-center gap-3 mt-6 sm:col-span-1 sm:mt-0">
            <span className="font-medium opacity-80">Status:</span>
            <span
              className={`font-medium ${formData.status ? "text-green-700" : "text-red-500"}`}
            >
              {formData.status ? "Active" : "Deactive"}
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="status"
                checked={formData.status}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-white peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[10px] after:bg-gray-400 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#051F33] peer-checked:after:bg-[#C9C3BF]"></div>
            </label>
          </div>
        </div>

        <div className="w-full grid grid-cols-12 gap-6 md:grid-cols-8 sm:grid-cols-4">
          <div className="col-span-6 flex flex-col gap-2">
            <label className="ml-2 font-medium opacity-80">Price</label>

            <div className="relative">
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="h-12 rounded-xl border-none outline-none px-4 pr-16 w-full shadow-sm"
              />

              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                CAD $
              </span>
            </div>
          </div>

          <div className="relative col-span-6 flex flex-col gap-2">
            <label className="ml-2 font-medium opacity-80">Category</label>
            <input
              type="text"
              readOnly
              value={
                categories.find((c) => c._id === formData.categoryId)?.name ||
                "Select category"
              }
              onClick={() => setShowCategory((prev) => !prev)}
              className="h-12 rounded-xl border-none outline-none px-4 w-full shadow-sm cursor-pointer bg-white"
            />
            {showCategory && (
              <div className="absolute top-24 flex flex-col z-50 w-full max-h-60 overflow-y-auto gap-1 p-2 rounded-xl bg-gray-100 shadow-md">
                {categories.map((cat) => (
                  <button
                    key={cat._id}
                    onClick={() => handleCategorySelect(cat._id)}
                    className="text-left px-3 py-2 rounded-lg hover:bg-gray-200"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="col-span-6 flex flex-col gap-2">
            <label className="ml-2 font-medium opacity-80">Count</label>

            <div className="flex gap-3 items-center sm:flex-col sm:items-stretch">
              <input
                type="number"
                name="count"
                value={formData.count}
                onChange={handleChange}
                className="h-12 rounded-xl border-none outline-none px-4 flex-1 sm:flex-none w-full shadow-sm"
              />

              <div className="flex rounded-xl bg-white p-1 shadow-sm shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      isBulk: false,
                    }))
                  }
                  className={`rounded-lg py-3 px-5 text-xs font-medium transition sm:flex-1 ${
                    !formData.isBulk
                      ? "bg-[#051F33] text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Per Item
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      isBulk: true,
                    }))
                  }
                  className={`rounded-lg py-3 px-5 text-xs font-medium transition sm:flex-1 ${
                    formData.isBulk
                      ? "bg-[#051F33] text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Per Kg
                </button>
              </div>
            </div>
          </div>

          {!typeSeller && (
            <>
              <div className="col-span-6 flex flex-col gap-2">
                <label className="ml-2 font-medium opacity-80">Calorie</label>
                <input
                  type="number"
                  name="calorie"
                  value={formData.calorie}
                  onChange={handleChange}
                  className="h-12 rounded-xl border-none outline-none px-4 w-full shadow-sm"
                />
              </div>

              <div className="relative col-span-6 flex flex-col gap-2">
                <label className="ml-2 font-medium opacity-80">Tastes</label>
                <div
                  onClick={() => setShowTaste((prev) => !prev)}
                  className="min-h-12 rounded-xl px-3 py-2 bg-white shadow-sm cursor-pointer flex flex-wrap gap-2 items-center"
                >
                  {formData.tastes.length > 0 ? (
                    formData.tastes.map((taste) => (
                      <div
                        key={taste}
                        className="bg-[#051F33] text-white px-3 py-1 rounded-full flex items-center gap-2"
                      >
                        <span>{taste}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTaste(taste);
                          }}
                        >
                          <IoCloseCircle size={18} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-400">Select tastes</span>
                  )}
                </div>
                {showTaste && (
                  <div className="absolute top-24 flex flex-col z-50 w-full max-h-60 overflow-y-auto gap-1 p-2 rounded-xl bg-gray-100 shadow-md">
                    {tastesList.map((taste) => (
                      <button
                        key={taste}
                        onClick={() => handleTasteSelect(taste)}
                        className={`text-left px-3 py-2 rounded-lg hover:bg-gray-200 ${formData.tastes.includes(taste) ? "bg-[#051F33] text-white" : ""}`}
                      >
                        {taste}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative col-span-6 flex flex-col gap-2">
                <label className="ml-2 font-medium opacity-80">
                  Nationality
                </label>
                <input
                  type="text"
                  readOnly
                  value={
                    nationalities.find((n) => n._id === formData.nationality)
                      ?.name ||
                    (formData.nationality ? "Loading..." : "Select nationality")
                  }
                  onClick={() => setShowNationality((prev) => !prev)}
                  className="h-12 rounded-xl border-none outline-none px-4 w-full shadow-sm cursor-pointer bg-white"
                />
                {showNationality && (
                  <div className="absolute top-24 flex flex-col z-50 w-full max-h-60 overflow-y-auto gap-1 p-2 rounded-xl bg-gray-100 shadow-md">
                    {nationalities.map((nationality) => (
                      <button
                        key={nationality._id}
                        type="button"
                        onClick={() => handleNationalitySelect(nationality._id)}
                        className={`text-left px-3 py-2 rounded-lg hover:bg-gray-200 ${
                          formData.nationality === nationality._id
                            ? "bg-[#051F33] text-white"
                            : ""
                        }`}
                      >
                        {nationality.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {typeSeller && (
            <div className="relative col-span-6 flex flex-col gap-2">
              <label className="ml-2 font-medium opacity-80">Brand</label>
              <input
                type="text"
                readOnly
                value={
                  brands.find((b) => b._id === formData.brand)?.name ||
                  "Select brand"
                }
                onClick={() => setShowBrand((prev) => !prev)}
                className="h-12 rounded-xl border-none outline-none px-4 w-full shadow-sm cursor-pointer bg-white"
              />
              {showBrand && (
                <div className="absolute top-24 flex flex-col z-50 w-full max-h-60 overflow-y-auto gap-1 p-2 rounded-xl bg-gray-100 shadow-md">
                  {brands.map((brand) => (
                    <button
                      key={brand._id}
                      onClick={() => handleBrandSelect(brand._id)}
                      className="text-left px-3 py-2 rounded-lg hover:bg-gray-200"
                    >
                      {brand.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="ml-2 font-medium opacity-80">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="rounded-2xl border-none outline-none p-4 w-full shadow-sm resize-none"
          />
        </div>
      </div>
      <button
          onClick={handleSubmit}
          disabled={submitting}
        className="bg-[#051F33] text-white px-7 py-2.5 rounded-2xl font-medium hover:bg-[#082a45] transition shadow-sm disabled:opacity-70 sm:w-full"
        >
          {submitting ? "Saving..." : "Save Changes"}
        </button>
    </div>
  );
}